import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Input,
  Container,
  Row,
  Col,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Label,
  FormGroup,
  Badge,
  CardHeader,
  Spinner,
  Alert,
} from "reactstrap";

import {
  FaBarcode,
  FaCalendarAlt,
  FaCartPlus,
  FaCreditCard,
  FaEnvelope,
  FaIdCard,
  FaImage,
  FaKeyboard,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaReceipt,
  FaSearch,
  FaShoppingCart,
  FaStore,
  FaTrash,
  FaTrashAlt,
  FaUser,
  FaWhatsapp,
  FaBuilding,
  FaSave,
} from "react-icons/fa";

import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import emptyCart from "../../assets/images/empty-cart.png";
import config from "../../helpers/config";
import "./POSPage.css";

import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import ClientSelectModal from "./ClientSelectModal";

import { productActions } from "../../sagas/productSlice";
import { saleActions } from "../../sagas/saleSlice";
import { clientActions } from "../../sagas/clientSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { commercialActions } from "../../sagas/commercialSlice";

import { buildEscPosReceipt } from "../../helpers/printUtility";

import {
  CBL_PAGE,
  CPMT_INVOICES_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";
import { authActions } from "../../sagas/authSlice";

/* -----------------------------
   FORM INITIAL VALUES + VALIDATION
----------------------------- */

const getTodayDate = () =>
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

const buildInitialValues = () => ({
  numero: "",
  commandeId: null,
  commercialId: null,
  commandeNumero: "",
  commandeDateCreation: getTodayDate(),
  clientId: null,
  chantierId: null,
  clientNom: "",
  clientAdresse: "",
  clientIce: "",
  clientTelephone1: "",
  clientTelephone2: "",
  clientEmail: "",
  deliveryNoteNb: "",
  estLivraisonDirecte: false,
  dateLivraison: getTodayDate(),
  lignes: [],
  montantCheque: 0,
  montantTotalHT: 0,
  montantTotalTTC: 0,
  montantTotalTaxe: 0,
  montantPaye: 0,
  montantPayeCash: 0,
  montantPayeCarte: 0,
  addresseLivraison: "",
  modeReglement: "ESPECES",
  dateOperation: getTodayDate(),
  entrepotId: null,
  transporteurId: null,
  transporteurNom: "",
  transporteurTelephone: "",
  status: "EN_ATTENTE",
  accuse: false,
  estFacture: false,
  numeroFacture: "",
  isClientParticulier: false,
  numeroCheque: "",
  banque: "",
  dateEcheanceCheque: null,
});

const validationSchema = Yup.object({
  clientId: Yup.number().nullable(),
  modeReglement: Yup.string()
    .required("Mode de règlement requis")
    .oneOf(
      ["ESPECES", "CHEQUE", "VIREMENT", "TRAITE", "CARTE_BANCAIRE", "EN_COMPTE"],
      "Mode de règlement invalide"
    ),
});


const SaleForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const editMode = location.state?.editMode;
  const deliveryNote = location.state?.deliveryNote;
  console.log(deliveryNote)
  /* -----------------------------
     STORE SELECTORS
  ----------------------------- */
  const montantTotalTTC = useSelector((state) => state.sales?.montantTotalTTC || 0);
  const montantTotalHT = useSelector((state) => state.sales?.montantTotalHT || 0);
  const montantTotalTaxe = useSelector((state) => state.sales?.montantTotalTaxe || 0);
  const cart = useSelector((state) => state.sales?.cart || []);
  const { currentShift, loading: shiftLoading, error: shiftError } = useSelector(
    (state) => state.shift
  );
  const role = useSelector((state) => state.auth.role);
  const { businessName, email, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const [searchMode, setSearchMode] = useState("ALL");
  const [searchModeModal, setSearchModeModal] = useState(false);
  const SEARCH_MODES = [
    { value: "ALL", label: "Tout" },
    { value: "LIBELLE", label: "Libellé" },
    { value: "CODE", label: "Code article" },
    { value: "BARCODE", label: "Code barre" },
  ];

  const {
    products,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((state) => state.products);

  const unites = useSelector((state) => state.products?.unites || []);
  const clients = useSelector((state) => state.clients?.clients || []);
  const { transporteurs } = useSelector((state) => state.transporteurs || {});
  const { entrepots } = useSelector((state) => state.entrepots || {});
  const { list: commercialList } = useSelector((state) => state.commercials || {});
  const [openShiftModal, setOpenShiftModal] = useState(false);

  const isAdmin = role === "ROLE_ADMIN";

  const safeProducts = useMemo(() => products || [], [products]);

  /* -----------------------------
     LOCAL STATE
  ----------------------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [modal, setModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [clearCartModal, setClearCartModal] = useState(false);
  const toggleClearCartModal = () => setClearCartModal((v) => !v);

  const [receiptData, setReceiptData] = useState(null);
  const [clientErrors, setClientErrors] = useState({});
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState(null);

  const [displayMode] = useState("card"); // 'card' | 'table'

  const [numPadOpen, setNumPadOpen] = useState(false);
  const [payKeypadOpen, setPayKeypadOpen] = useState(false);

  const [numPadConfig, setNumPadConfig] = useState({
    title: "",
    value: "",
    index: null,
    field: null,
  });

  const [showKeypad, setShowKeypad] = useState(false);
  const [clientTextKeypad, setClientTextKeypad] = useState({
    isOpen: false,
    field: null,
    title: "Clavier",
  });

  const [clientSelectModal, setClientSelectModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [selectedEntrepot, setSelectedEntrepot] = useState(null);
  const [selectedTransporteur, setSelectedTransporteur] = useState(null);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const isEdit = useRef(false);
  const [client, setClient] = useState({
    nom: '',
    prenom: '',
    cin: '',
    telephone1: '',
    telephone2: '',
    email: '',
    adresse: '',
    ville: 'Casablanca',
    typeClient: 'ENTREPRISE',
    codePostal: '',
    plafond: 0,
    entreprise: '', // New field for "Entreprise"
    ice: '',// New field for "ICE",
    active: true,
    solde: 0 // Ce champ sera en lecture seule
  });

  const isKeyboardLocked = showKeypad || paymentModal || numPadOpen;

  /* -----------------------------
     REFS
  ----------------------------- */
  const searchInputRef = useRef(null);
  const lastAddedRef = useRef(null);

  const productsRef = useRef([]);
  const searchTermRef = useRef("");
  const scanBufferRef = useRef("");
  const scanTimeoutRef = useRef(null);
  const pendingScanResolveRef = useRef(null);

  const isShiftBlocked = !shiftLoading && (!currentShift || currentShift.status === "CLOSED");
  const canEditBL = (!editMode || deliveryNote?.status === "EN_PREPARATION") && !isShiftBlocked;

  useEffect(() => {
    productsRef.current = safeProducts;
  }, [safeProducts]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const focusSearchInput = useCallback(() => {
    if (isKeyboardLocked) return;

    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [isKeyboardLocked]);

  useEffect(() => {
    if (isKeyboardLocked) {
      searchInputRef.current?.blur();
      return;
    }

    const focusTimer = setTimeout(() => {
      focusSearchInput();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [focusSearchInput, isKeyboardLocked]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        focusSearchInput();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [focusSearchInput]);


  useEffect(() => {
    return () => dispatch(saleActions.clearCart());
  }, []);

  const currentSearchModeLabel = SEARCH_MODES.find((m) => m.value === searchMode)?.label || "Tout";

  /* -----------------------------
     UTILITIES
  ----------------------------- */
  const formatPrice = (amount, currency = true) => {
    const formatted = new Intl.NumberFormat("fr-MA", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));

    return currency ? `${formatted} DH` : formatted;
  };


  const buildSearchQuery = (term, mode) => {
    const q = (term || "").trim();
    if (!q) return "";

    return q;
  };

  const refreshProducts = useCallback(
    (term, page = 0, mode = "ALL") => {
      dispatch(
        productActions.findAll({
          search: buildSearchQuery(term, mode),
          page,
          size: 200,
        })
      );
    },
    [dispatch]
  );

  const clearSearchField = useCallback(() => {
    debouncedSearchRef.current?.cancel?.();
    scanBufferRef.current = "";
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    if (pendingScanResolveRef.current) {
      clearTimeout(pendingScanResolveRef.current);
    }
    setSearchTerm("");
    setCurrentPage(1);
    refreshProducts("", 0, searchMode);
    focusSearchInput();
  }, [focusSearchInput, refreshProducts, searchMode]);

  const isLikelyBarcodeInput = useCallback((value) => {
    const normalized = String(value || "").trim();
    return normalized.length >= 8 && /^\d+$/.test(normalized);
  }, []);

  const findExactProductMatch = useCallback((term) => {
    const normalized = String(term || "").trim().toLowerCase();
    if (!normalized) return null;

    return productsRef.current.find((product) =>
      [
        product?.codeBarre,
        product?.barcode,
        product?.code,
        product?.articleCode,
      ].some((value) => String(value || "").trim().toLowerCase() === normalized)
    ) || null;
  }, []);

  const handleAddToCart = useCallback((product) => {
    if (!canEditBL) {
      Swal.fire({
        icon: "warning",
        title: "Modification non autorisÃ©e",
        text: "Ce bon de livraison n'est plus modifiable.",
      });
      return;
    }

    dispatch(saleActions.addToCart(product));
  }, [canEditBL, dispatch]);

  const resolveScannedProduct = useCallback(
    (rawValue) => {
      const scannedValue = String(rawValue || "").trim();
      if (!scannedValue) return;

      const localMatch = findExactProductMatch(scannedValue);
      if (localMatch) {
        handleAddToCart(localMatch);
        clearSearchField();
        return;
      }

      dispatch(
        productActions.checkBarcode({
          barcode: scannedValue,
          onSuccess: (product) => {
            if (!product) return;
            handleAddToCart(product);
            clearSearchField();
          },
        })
      );
    },
    [clearSearchField, dispatch, findExactProductMatch, handleAddToCart]
  );


  const getModeReglementLabel = (mode) => {
    switch (mode) {
      case "ESPECES":
        return "Espèces";
      case "CARTE_BANCAIRE":
        return "Carte bancaire";
      case "CHEQUE":
        return "Chèque";
      case "VIREMENT":
        return "Virement";
      case "EN_COMPTE":
        return "En compte";
      case "TRAITE":
        return "Traite";
      default:
        return mode || "-";
    }
  };


  const getTotalQuantitiesFromReceipt = (data) => {
    if (!data?.lignes?.length) return 0;
    return data.lignes.reduce((sum, l) => sum + Number(l.quantiteLivree || 0), 0);
  };


  /* -----------------------------
     FETCH INITIAL DATA
  ----------------------------- */
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
    dispatch(transporteurActions.list());
    dispatch(entrepotActions.list());
    dispatch(commercialActions.list({ query: "" }));
  }, [dispatch]);

  /* -----------------------------
     FETCH PRODUCTS ON PAGE / MODE CHANGE
  ----------------------------- */
  useEffect(() => {
    refreshProducts(searchTermRef.current, currentPage - 1, searchMode);
  }, [currentPage, refreshProducts, searchMode]);

  /* -----------------------------
     CLIENT OPTIONS
  ----------------------------- */
  const clientOptions = useMemo(() => {
    const options = (clients || []).map((c) => ({
      solde: c.solde,
      totalDebit: c.totalDebit,
      totalCredit: c.totalCredit,
      chantiers: c.chantiers,
      value: c.id,
      label: c.typeClient === "PARTICULIER" ? `${c.prenom} ${c.nom}` : c.entreprise,
    }));

    return options;

  }, [clients]);

  const defaultPassagerClient = useMemo(() => {
    const passager = (clients || []).find((c) => {
      const nom = String(c?.nom || "").trim().toUpperCase();
      const prenom = String(c?.prenom || "").trim().toUpperCase();
      return nom === "PASSAGER" || prenom === "PASSAGER";
    });

    if (!passager) return null;

    return {
      solde: passager.solde,
      totalDebit: passager.totalDebit,
      totalCredit: passager.totalCredit,
      chantiers: passager.chantiers,
      value: passager.id,
      label:
        passager.typeClient === "PARTICULIER"
          ? `${passager.prenom || ""} ${passager.nom || ""}`.trim()
          : passager.entreprise,
    };
  }, [clients]);

  useEffect(() => {
    if (editMode) return;
    if (selectedClient || !defaultPassagerClient) return;

    setSelectedClient(defaultPassagerClient);
  }, [defaultPassagerClient, editMode, selectedClient]);


  const updatePaymentAmounts = (changedField, value) => {
    const total = Number(montantTotalTTC) || 0;
    const val = Number(value) || 0;

    if (changedField === "cash") {
      const carte = Math.max(total - val, 0);

      formik.setFieldValue("montantPayeCash", val);
      formik.setFieldValue("montantPayeCarte", carte);
      formik.setFieldValue("montantPaye", val + carte);
    }

    if (changedField === "card") {
      const cash = Math.max(total - val, 0);

      formik.setFieldValue("montantPayeCarte", val);
      formik.setFieldValue("montantPayeCash", cash);
      formik.setFieldValue("montantPaye", val + cash);
    }
  };

  /* -----------------------------
     CART PAGINATION
  ----------------------------- */
  const cartItemsPerPage = 100;
  const cartTotalPages = Math.ceil(cart.length / cartItemsPerPage);
  const [cartPage, setCartPage] = useState(1);

  const cartStartIndex = (cartPage - 1) * cartItemsPerPage;
  const cartEndIndex = cartStartIndex + cartItemsPerPage;
  const currentCartItems = cart.slice(cartStartIndex, cartEndIndex);

  /* -----------------------------
     CART ACTIONS
  ----------------------------- */
  /* const unusedHandleAddToCart = useCallback((product) => {
    if (!canEditBL) {
      Swal.fire({
        icon: "warning",
        title: "Modification non autorisée",
        text: "Ce bon de livraison n'est plus modifiable.",
      });
      return;
    }

    dispatch(saleActions.addToCart(product));
  }, [canEditBL, dispatch]); */

  const handleInputChange = useCallback(
    (index, event) => {
      const { name, value } = event.target;
      dispatch(saleActions.handleInputChange({ index, name, value }));
    },
    [dispatch]
  );

  const handleRemoveFromCart = useCallback(
    (productId) => {
      dispatch(saleActions.removeFromCart(productId));

      setCartPage((prev) => {
        const newCartLength = cart.length - 1;
        const newTotalPages = Math.ceil(newCartLength / cartItemsPerPage);
        return prev > newTotalPages ? Math.max(1, newTotalPages) : prev;
      });
    },
    [dispatch, cart.length]
  );

  const handleClearCart = () => {
    dispatch(saleActions.clearCart());
    setCartPage(1);
  };

  /* -----------------------------
     DEBOUNCED SEARCH (FIXED)
  ----------------------------- */
  const debouncedSearchRef = useRef(
    debounce((term, page, mode) => {
      refreshProducts(term, page, mode);
    }, 700)
  );

  useEffect(() => {
    return () => {
      // cleanup debounce on unmount
      debouncedSearchRef.current?.cancel?.();
    };
  }, []);

  useEffect(() => {
    if (isKeyboardLocked) return undefined;

    const isEditableElement = (element) => {
      if (!element) return false;
      const tagName = element.tagName?.toLowerCase();
      return tagName === "input" || tagName === "textarea" || element.isContentEditable;
    };

    const handleWindowScan = (event) => {
      if (showKeypad || searchModeModal) return;

      const activeElement = document.activeElement;
      if (activeElement === searchInputRef.current || isEditableElement(activeElement)) {
        return;
      }

      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      if (pendingScanResolveRef.current) {
        clearTimeout(pendingScanResolveRef.current);
      }

      if (event.key === "Enter") {
        if (scanBufferRef.current.length >= 3) {
          const scannedValue = scanBufferRef.current;
          resolveScannedProduct(scannedValue);
          if (isLikelyBarcodeInput(scannedValue)) {
            clearSearchField();
          }
        }
        scanBufferRef.current = "";
        return;
      }

      if (event.key.length === 1) {
        scanBufferRef.current += event.key;
        setSearchTerm(scanBufferRef.current);
        scanTimeoutRef.current = setTimeout(() => {
          scanBufferRef.current = "";
        }, 120);
      }
    };

    window.addEventListener("keydown", handleWindowScan);
    return () => {
      window.removeEventListener("keydown", handleWindowScan);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      if (pendingScanResolveRef.current) {
        clearTimeout(pendingScanResolveRef.current);
      }
    };
  }, [clearSearchField, isKeyboardLocked, isLikelyBarcodeInput, resolveScannedProduct, searchModeModal, showKeypad]);

  useEffect(() => {
    const trimmedSearch = String(searchTerm || "").trim();

    if (!trimmedSearch || showKeypad || searchModeModal || isKeyboardLocked) {
      if (pendingScanResolveRef.current) {
        clearTimeout(pendingScanResolveRef.current);
      }
      return undefined;
    }

    const exactMatch = findExactProductMatch(trimmedSearch);
    const shouldAutoResolve = Boolean(exactMatch) || searchMode === "BARCODE";

    if (!shouldAutoResolve) return undefined;

    pendingScanResolveRef.current = setTimeout(() => {
      resolveScannedProduct(trimmedSearch);
    }, 180);

    return () => {
      if (pendingScanResolveRef.current) {
        clearTimeout(pendingScanResolveRef.current);
      }
    };
  }, [
    findExactProductMatch,
    isKeyboardLocked,
    resolveScannedProduct,
    searchMode,
    searchModeModal,
    searchTerm,
    showKeypad,
  ]);


  /* -----------------------------
     AUTO-OPEN QTY KEYPAD AFTER ADD
  ----------------------------- */
  useEffect(() => {
    if (!lastAddedRef.current) return;
    if (!cart || cart.length === 0) return;

    const lastId = lastAddedRef.current;
    const idx = cart.findIndex((c) => c.articleId === lastId || c.id === lastId);
    if (idx === -1) return;

    setNumPadConfig({
      title: `Quantité - ${cart[idx].articleNom}`,
      value: cart[idx].quantite,
      index: idx,
      field: "quantite",
    });
    setNumPadOpen(true);

    lastAddedRef.current = null;
  }, [cart]);

  /* -----------------------------
     FORMIK
  ----------------------------- */
  const initialValues = useMemo(() => buildInitialValues(), []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleCreateSale(values);
      resetForm({ values: buildInitialValues() });
    },
  });

  const handleKeypadConfirm = (value) => {
    if (keypadTarget === "cash") {
      updatePaymentAmounts("cash", value);
    }

    if (keypadTarget === "card") {
      updatePaymentAmounts("card", value);
    }
  };

  /* -----------------------------
     CREATE SALE
  ----------------------------- */
  const handleCreateSale = (values) => {
    // Walk-in validation
    if (!selectedClient) {
      if (values.modeReglement === "EN_COMPTE") {
        Swal.fire({
          icon: "warning",
          title: t("sale.error"),
          text: "Impossible de vendre en compte sans sélectionner un client.",
        });
        return;
      }

      if (values.modeReglement === "ESPECES") {
        if (!values.montantPaye) {
          Swal.fire({
            icon: "warning",
            title: t("sale.error"),
            text: t("payment.amountRequiredForWalkIn"),
          });
          return;
        }

        if (Number(values.montantPaye) < Number(montantTotalTTC)) {
          Swal.fire({
            icon: "warning",
            title: t("sale.error"),
            text: t("payment.amountMustEqualTotal"),
          });
          return;
        }
      } else {
        // carte/cheque/virement -> montant auto = total
        values.montantPaye = montantTotalTTC;
      }
    }

    if (!cart?.length) {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: t("cart.empty"),
      });
      return;
    }

    if (!currentShift) {
      Swal.fire({
        icon: "warning",
        title: t("sale.error"),
        text: t("shifts.noShifts"),
      });
      return;
    }

    const saleData = {
      clientId: selectedClient ? selectedClient.value : null,
      chantierId: selectedChantier ? selectedChantier.value : null,
      entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
      commercialId: selectedCommercial ? selectedCommercial.value : null,
      shiftId: currentShift?.id,
      isComptoir: true,
      isPassager: selectedClient ? false : true,
      transporteurId: selectedTransporteur ? selectedTransporteur.value : null,
      modeReglement: values.modeReglement,
      dateOperation: values.dateOperation,
      dateLivraison: values.dateLivraison,
      montantTotalHT,
      montantTotalTTC,
      montantTotalTaxe,
      montantPaye:
        values.modeReglement === "EN_COMPTE"
          ? 0
          : (values.montantPaye > montantTotalTTC ? montantTotalTTC : values.montantPaye),
      status: "EN_PREPARATION",
      lignes: cart.map((item) => ({
        articleId: item.articleId,
        articleCode: item.articleCode,
        articleNom: item.articleNom,
        quantiteLivree: item.quantite,
        unite: unites.find((u) => u.code === item.unite)?.libelle || item.unite || "",
        prixUnitaireTTC: item.prixUnitaireTTC,
        prixUnitaireHT: item.prixUnitaireHT,
        montantHT: item.montantHT,
        montantTTC: item.montantTTC,
        tauxTVA: item.tauxTVA,
      })),
      numeroCheque: values.modeReglement === "CHEQUE" ? values.numeroCheque : null,
      banque: values.modeReglement === "CHEQUE" ? values.banque : null,
      dateEcheanceCheque: values.modeReglement === "CHEQUE" ? values.dateEcheanceCheque : null,
      referenceVirement: values.modeReglement === "VIREMENT" ? values.referenceVirement : null,
    };

    if (editMode && deliveryNote?.id) {
      dispatch(deliveryActions.update({
        id: deliveryNote.id,
        data: saleData,
        onSuccess: (data) => {
          Swal.fire({ icon: "success", title: "BL mis à jour" });
          navigate(CBL_PAGE);
        }
      }));
    } else {
      dispatch(
        deliveryActions.add({
          data: saleData,
          onSuccess: (data) => {
            Swal.fire({
              icon: "success",
              title: t("common.saved"),
              text: t("bonLivraison.savedSuccess"),
            });

            setReceiptData(data);
            setModal(true);

            dispatch(saleActions.clearCart());
            setPaymentModal(false);
            setSelectedClient(null);
            setSelectedCommercial(null);
            setSelectedEntrepot(null);
            setSelectedTransporteur(null);
            setSelectedChantier(null);

            formik.resetForm({ values: buildInitialValues() });

            dispatch(
              productActions.findAll({
                search: "",
                page: currentPage - 1,
                size: 200,
              })
            );
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: t("sale.error"),
              text: error?.message || t("sale.errorMessage"),
            });
          },
        })
      );
    }
  };


  /* -----------------------------
     PRINT UTILITIES
  ----------------------------- */
  const printEscPosReceipt = (data) => {
    const escpos = buildEscPosReceipt(data);
    const encoded = encodeURIComponent(escpos);
    window.location.href = `rawbt://print?text=${encoded}`;
  };

  const generateThermiquePDF = async () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 350],
      });

      const pageWidth = 80;
      const margin = 4;
      let y = 8;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);

      const companyTitle = businessName || "MATJARPOS";
      const titleLines = doc.splitTextToSize(companyTitle, pageWidth - margin * 2);

      titleLines.forEach((line, i) => {
        doc.text(line, pageWidth / 2, y + i * 7, { align: "center" });
      });

      y += titleLines.length * 7 + 3;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      if (address) {
        const lines = doc.splitTextToSize(address, pageWidth - margin * 2);
        lines.forEach((l, i) => doc.text(l, pageWidth / 2, y + i * 5, { align: "center" }));
        y += lines.length * 5;
      }

      if (telephone) {
        doc.text(telephone, pageWidth / 2, y + 5, { align: "center" });
        y += 8;
      }

      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("REÇU DE VENTE", pageWidth / 2, y, { align: "center" });
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      doc.text(`N° : ${receiptData?.numero || "-"}`, margin, y);
      y += 6;
      doc.text(`Date : ${receiptData?.createdAt || receiptData?.dateOperation || "-"}`, margin, y);
      y += 6;

      doc.text(`Vendeur : ${receiptData?.createdBy || "---"}`, margin, y);
      y += 6;

      doc.text(`Client : ${receiptData?.clientNom || "Passager"}`, margin, y);
      y += 6;

      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);

      doc.text("Article", margin, y);
      doc.text("Qté", pageWidth - 42, y, { align: "center" });
      doc.text("PU", pageWidth - 26, y, { align: "right" });
      doc.text("Total", pageWidth - margin, y, { align: "right" });

      y += 4;
      doc.line(margin, y, pageWidth - margin, y);
      y += 4;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      receiptData?.lignes?.forEach((item) => {
        const name =
          item.articleNom?.length > 20 ? item.articleNom.slice(0, 20) + "…" : item.articleNom;

        const qte = Number(item.quantiteLivree || 0);
        const pu = Number(item.prixUnitaireTTC || 0);
        const total = Number(item.montantTTC || qte * pu);

        doc.text(name || "-", margin, y);
        doc.text(String(qte), pageWidth - 42, y, { align: "center" });
        doc.text(formatPrice(pu, false), pageWidth - 26, y, { align: "right" });
        doc.text(formatPrice(total, false), pageWidth - margin, y, { align: "right" });

        y += 7;
      });

      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("TOTAL TTC", margin, y);
      doc.text(formatPrice(receiptData?.montantTotalTTC, false), pageWidth - margin, y, {
        align: "right",
      });

      y += 8;
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      const modeLabel = getModeReglementLabel(receiptData?.modeReglement);
      doc.text(`Mode paiement : ${modeLabel}`, margin, y);
      y += 6;

      doc.text("Montant payé :", margin, y);
      doc.text(formatPrice(receiptData?.montantPaye, false), pageWidth - margin, y, {
        align: "right",
      });
      y += 6;

      if (receiptData?.modeReglement === "ESPECES" && receiptData?.montantPaye > receiptData?.montantTotalTTC) {
        doc.text("Monnaie :", margin, y);
        doc.text(
          formatPrice(receiptData.montantPaye - receiptData.montantTotalTTC, false),
          pageWidth - margin,
          y,
          { align: "right" }
        );
        y += 6;
      }

      doc.line(margin, y, pageWidth - margin, y);
      y += 7;

      doc.setFontSize(11);
      doc.text("Présentez ce ticket en cas de retour", pageWidth / 2, y, { align: "center" });
      y += 6;
      doc.text("ou échange dans un delai de 7 jours", pageWidth / 2, y, { align: "center" });
      y += 6;
      doc.text("Merci de votre confiance", pageWidth / 2, y, { align: "center" });
      y += 6;

      doc.text("Powered by MatjarPOS", pageWidth / 2, y, { align: "center" });

      const blobUrl = doc.output("bloburl");
      window.open(blobUrl, "_blank");
    } catch (e) {
      console.error(e);
    }
  };

  const generatePDF = async (pdfFormat) => {
    if (pdfFormat === "THERMIQUE") {
      const isAndroid = /Android/i.test(navigator.userAgent);
      if (isAndroid) {
        printEscPosReceipt({
          businessName,
          address,
          telephone,
          receiptData,
        });
      } else {
        await generateThermiquePDF();
      }
    } else {
      await generateThermiquePDF();
    }
  };

  const openClientTextKeypad = useCallback((field, title) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setClientTextKeypad({
      isOpen: true,
      field,
      title,
    });
  }, []);

  const closeClientTextKeypad = useCallback(() => {
    setClientTextKeypad({
      isOpen: false,
      field: null,
      title: "Clavier",
    });
  }, []);

  const handleClientTextKeypadChange = useCallback(
    (value) => {
      if (!clientTextKeypad.field) return;

      setClient((prev) => ({
        ...prev,
        [clientTextKeypad.field]: value,
      }));

      setClientErrors((prev) => {
        if (!prev[clientTextKeypad.field]) return prev;
        const next = { ...prev };
        delete next[clientTextKeypad.field];
        return next;
      });
    },
    [clientTextKeypad.field]
  );

  const handleSubmitClient = (e) => {
    e.preventDefault();

    const newErrors = {};
    
    // Validations for "ENTREPRISE"
    if (client.typeClient === 'ENTREPRISE') {
      if (!client.entreprise) newErrors.entreprise = 'Nom de l\'entreprise obligatoire';
    }


    if (Object.keys(newErrors).length > 0) {
      setClientErrors(newErrors);
      return;
    }

    let payload = {
      data: {
        ...client,
        nom: client.entreprise,
        prenom: client.entreprise,
        chantiers: [],
      },

      onSuccess: (createdClient) => {
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Client enregistré avec succès !",
          timer: 1200,
          showConfirmButton: false,
        });

        // ✅ refresh list from backend
        dispatch(clientActions.search({ query: "" }));

        // ✅ reset form
        setClient({
          nom: "",
          prenom: "",
          cin: "",
          telephone1: "",
          telephone2: "",
          typeClient: "PARTICULIER",
          email: "",
          adresse: "",
          ville: "Casablanca",
          codePostal: "",
          plafond: 0,
          entreprise: "",
          ice: "",
          solde: 0,
          active: true,
        });

        // ✅ close create modal + reopen select modal
        closeClientTextKeypad();
        setShowClientModal(false);

        setTimeout(() => {
          setClientSelectModal(true);
        }, 250);
      },
      onError: (error) => {
        Swal.fire("Erreur", error, "error");
      }
    };

    dispatch(clientActions.addClient(payload));

  }


  useEffect(() => {
    if (editMode && deliveryNote && !isEdit.current) {
      isEdit.current = true;

      // 1️⃣ Fill client
      if (deliveryNote.clientId) {
        const foundClient = clientOptions.find(c => c.value === deliveryNote.clientId);
        if (foundClient) setSelectedClient(foundClient);
      }

      // 2️⃣ Fill commercial
      if (deliveryNote.commercialId) {
        const foundCommercial = commercialList?.find(c => c.id === deliveryNote.commercialId);
        if (foundCommercial) {
          setSelectedCommercial({
            value: foundCommercial.id,
            label: foundCommercial.fullName
          });
        }
      }

      formik.setFieldValue('montantPaye', deliveryNote.modeReglement === 'CHEQUE' ? deliveryNote.montantCheque : deliveryNote.montantPaye);
      formik.setFieldValue('modeReglement', deliveryNote.modeReglement);

      // 3️⃣ Fill warehouse
      if (deliveryNote.entrepotId) {
        const foundEntrepot = entrepots?.find(e => e.id === deliveryNote.entrepotId);
        if (foundEntrepot) {
          setSelectedEntrepot({
            value: foundEntrepot.id,
            label: foundEntrepot.nom
          });
        }
      }

      // 4️⃣ Load BL lines into cart
      const cartItems = deliveryNote.lignes.map(l => ({
        articleId: l.articleId,
        articleCode: l.articleCode,
        articleNom: l.articleNom,
        quantite: l.quantiteLivree,
        prixUnitaireTTC: l.prixUnitaireTTC,
        prixUnitaireHT: l.prixUnitaireHT,
        montantHT: l.montantHT,
        montantTTC: l.montantTTC,
        tauxTVA: l.tauxTVA,
        unite: l.unite
      }));
      dispatch(saleActions.setCart(cartItems));
    }
  }, [editMode, deliveryNote, clientOptions, commercialList, entrepots, dispatch]);


  /* -----------------------------
     VIEWS
  ----------------------------- */

  const TableView = () => (
    <>
      <div
        className="cart-container"
        style={{
          height: "400px",
          overflowY: "auto",
        }}
      >
        <Table bordered hover>
          <thead>
            <tr>
              <th style={{ width: 350 }}>{t("product.description")}</th>
              <th className="text-end">{t("product.stockDisponible")}</th>
            </tr>
          </thead>
          <tbody>
            {safeProducts.length === 0 ? (
              <tr>
                <td colSpan="2" className="text-center py-4">
                  <div className="text-muted">{t("product.noProducts")}</div>
                </td>
              </tr>
            ) : (
              safeProducts.map((product) => (
                <tr
                  key={product.id}
                  onClick={() => handleAddToCart(product)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ maxWidth: 350 }}>
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-uppercase" style={{ fontSize: "0.9rem" }}>
                        {product.libelle}
                      </span>

                      <small className="text-muted d-flex justify-content-between mt-1">
                        <span>{unites.find((u) => u.code === product.unite)?.libelle || ""}</span>
                        <span className="fw-bold text-primary">
                          {formatPrice(product.prixVenteTTC1, false)}
                        </span>
                      </small>
                    </div>
                  </td>

                  <td className="align-middle text-end fw-semibold">{product.stockDisponible}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          {t("product.showing", {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage,
            totalPages,
          })}
        </small>
      </div>
    </>
  );

  const CardView = () => (
    <div className="pos-products-container">
      <Row xs={2} md={3} lg={4} className="g-3">
        {safeProducts.length === 0 && (
          <Col xs={12} className="text-center py-5 text-muted">
            {t("product.noProducts")}
          </Col>
        )}

        {safeProducts.map((product) => {
          const unitLabel = unites.find((u) => u.code === product.unite)?.libelle || "";

          return (
            <Col key={product.id}>
              <div className="pos-product-card" onClick={() => handleAddToCart(product)}>
                <div className="pos-product-image">
                  {product.photo ? (
                    <img src={`${config.baseURL}${product.photo}`} alt={product.libelle} />
                  ) : (
                    <FaImage size={28} className="text-muted" />
                  )}
                </div>

                <div className="pos-product-body">
                  <div className="pos-product-title">{product.libelle}</div>
                  <div className="pos-product-code">{product.codeBarre}</div>

                  <div className="pos-product-meta">
                    <span className="unit">{unitLabel}</span>

                    <span
                      className={`stock ${product.stockDisponible <= 0 ? "out" : product.stockDisponible < 5 ? "low" : "ok"
                        }`}
                    >
                      {product.stockDisponible}
                    </span>
                  </div>

                  <div className="pos-product-footer">
                    <span className="price">{formatPrice(product.prixVenteTTC1)}</span>

                    <button
                      className="add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product);
                      }}
                      title={t("cart.add")}
                    >
                      <FaCartPlus />
                    </button>
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );

  /* -----------------------------
     RENDER
  ----------------------------- */
  return (
    <Container fluid>
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        fullName={fullName}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        isAdmin={isAdmin}
        activeTab={"VENTES"}
        onChangeTab={(tab) => {
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
        onCloseShift={handleClearCart}

      />

      <Row className="pos-main-layout g-3">
        {/* LEFT */}
        <Col md={8} className="pos-left">
          <Card className="shadow-sm rounded-4 border-0 h-100">
            <CardBody className="h-100 d-flex flex-column">
              {/* Search */}
              <div className="mb-3 position-relative d-flex gap-2">
                <div className="flex-grow-1 position-relative">
                  <Input
                    innerRef={searchInputRef}
                    type="text"
                    value={searchTerm}
                    autoFocus
                    inputMode="none"
                    onTouchStart={(e) => {
                      e.preventDefault();
                      focusSearchInput();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchTerm(value);
                      debouncedSearchRef.current(value, 0, searchMode);
                      setCurrentPage(1);
                    }}
                    onBlur={() => {
                      if (!isKeyboardLocked && !showKeypad && !searchModeModal) {
                        focusSearchInput();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const submittedValue = String(searchTerm || "").trim();
                        resolveScannedProduct(submittedValue);
                        if (isLikelyBarcodeInput(submittedValue) || searchMode === "BARCODE") {
                          clearSearchField();
                        }
                      }
                    }}
                    className="pos-nokeyboard"
                  />
                  <FaSearch className="pos-search-icon" />
                  <FaBarcode className="pos-barcode-icon" />
                </div>

                <Button
                  color="light"
                  className="rounded-3 fw-bold border"
                  style={{ minWidth: 120 }}
                  onClick={() => setSearchModeModal(true)}
                  title="Mode de recherche"
                >
                  {currentSearchModeLabel}
                </Button>

                <Button
                  color="secondary"
                  className="rounded-3 fw-bold"
                  style={{ minWidth: 60 }}
                  onClick={() => setShowKeypad(true)}
                  title="Clavier"
                >
                  <FaKeyboard />
                </Button>
              </div>

              {/* Products */}
              <div className="flex-grow-1 min-h-0">
                {displayMode === "table" ? <TableView /> : <CardView />}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT */}
        <Col md={4} className="pos-right">
          {/* CLIENT */}
          <Card className="shadow-sm rounded-4 border-0 mb-1">
            <CardBody>
              <FormGroup>
                <Label className="fw-bold text-secondary d-flex justify-content-between align-items-center">
                  <span>
                    <FaUser className="me-1" /> {t("commande.client")}
                  </span>
                </Label>

                <Button
                  color="light"
                  className="w-100 text-start fw-bold py-2 rounded-3 border"
                  disabled={!canEditBL}
                  onClick={() => setClientSelectModal(true)}
                >
                  {selectedClient ? selectedClient.label : t("common.selectOption")}
                </Button>
              </FormGroup>

              {/* {selectedClient && (
                <div className="pos-client-summary mt-3">
                  <div>
                    <small>Débit</small>
                    <strong className="text-danger">{formatPrice(selectedClient.totalDebit)}</strong>
                  </div>
                  <div>
                    <small>Crédit</small>
                    <strong className="text-success">{formatPrice(selectedClient.totalCredit)}</strong>
                  </div>
                  <div>
                    <small>Solde</small>
                    <strong>{formatClientSolde(selectedClient.totalDebit, selectedClient.totalCredit)}</strong>
                  </div>
                </div>
              )} */}
            </CardBody>
          </Card>

          {/* CART */}
          <Card className="shadow-sm rounded-4 border-0 h-100 pos-cart-card">
            <CardHeader className="bg-light fw-bold d-flex justify-content-between align-items-center flex-shrink-0">
              <span>
                <FaShoppingCart className="me-2" />
                {t("cart.title")}
              </span>

              <div className="d-flex align-items-center gap-2">
                {cart.length > 0 && <Badge color="primary">{cart.length}</Badge>}

                {cart.length > 0 && canEditBL && (
                  <Button color="danger" size="sm" outline onClick={toggleClearCartModal}>
                    <FaTrash />
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardBody className="p-0 d-flex flex-column">
              <div className="pos-cart-items flex-grow-1">
                {cart.length === 0 ? (
                  <div className="text-center text-muted py-4">{t("cart.empty")}</div>
                ) : (
                  currentCartItems.map((item, index) => (
                    <div
                      key={item.articleId}
                      className="pos-cart-line d-flex justify-content-between align-items-center"
                    >
                      <div className="flex-grow-1 me-2">
                        <strong className="d-block">{item.articleNom}</strong>

                        <div className="d-flex gap-2 mt-1">
                          {/* Qty */}
                          <Input
                            type="text"
                            value={item.quantite}
                            readOnly
                            inputMode="none"
                            disabled={!canEditBL}
                            onKeyDown={(e) => e.preventDefault()}
                            className="pos-cart-input pos-nokeyboard"
                            onClick={() => {
                              if (!canEditBL) return;
                              setNumPadConfig({
                                title: `Quantité - ${item.articleNom}`,
                                value: item.quantite,
                                index: cartStartIndex + index,
                                field: "quantite",
                              });
                              setNumPadOpen(true);
                            }}
                          />

                          {/* Price */}
                          <Input
                            type="text"
                            value={item.prixUnitaireTTC}
                            readOnly
                            inputMode="none"
                            onKeyDown={(e) => e.preventDefault()}
                            disabled={!canEditBL}
                            className="pos-cart-input pos-nokeyboard"
                            onClick={() => {
                              setNumPadConfig({
                                title: `Prix - ${item.articleNom}`,
                                value: item.prixUnitaireTTC,
                                index: cartStartIndex + index,
                                field: "prixUnitaireTTC",
                              });
                              setNumPadOpen(true);
                            }}
                          />
                        </div>
                      </div>

                      <div className="text-end d-flex flex-column align-items-end">
                        <strong>{formatPrice(item.montantTTC, false)}</strong>

                        <Button
                          color="danger"
                          size="sm"
                          outline
                          disabled={!canEditBL}
                          className="mt-1"
                          onClick={() => {
                            if (!canEditBL) return;
                            handleRemoveFromCart(item.articleId);
                          }}
                        >
                          <FaTrashAlt />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="pos-cart-footer flex-shrink-0">
                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>{t("cart.totalTTC")}</span>
                  <span>{formatPrice(montantTotalTTC)}</span>
                </div>

                {/* ACTIONS */}
                <div className="d-flex gap-2 mt-3">

                  {/* 💰 CHECKOUT */}
                  <Button
                    color="success"
                    size="lg"
                    className="w-100 fw-bold"
                    disabled={!canEditBL}
                    onClick={() => {
                      if (!cart?.length) return;

                      if (!selectedClient) {
                        Swal.fire({
                          icon: "info",
                          title: "Client non sélectionné",
                          text: t("payment.clientRequiredForWalkIn"),
                        });
                        return;
                      }

                      setPaymentModal(true);
                    }}
                  >
                    {canEditBL ? t("cart.checkout") : "BL Verrouillé"}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* -----------------------------
         RECEIPT MODAL
      ----------------------------- */}
      <Modal isOpen={modal} toggle={() => setModal(false)} className="pos-receipt-modal">
        <ModalHeader toggle={() => setModal(false)}>{t("receipt.title")}</ModalHeader>

        <ModalBody className="p-0 d-flex flex-column">
          <div id="receipt-content" className="pos-receipt-content p-4">
            <div className="text-center mb-4">
              <h5 className="mb-2">
                <FaStore className="me-2 text-secondary" />
                {businessName}
              </h5>
              <small className="d-block mb-2">
                <FaMapMarkerAlt className="me-1 text-secondary" />
                {address}
              </small>
              <div className="d-flex justify-content-center align-items-center gap-3">
                {telephone && (
                  <small>
                    <FaWhatsapp className="me-1 text-secondary" />
                    {telephone}
                  </small>
                )}
                <small>
                  <FaEnvelope className="me-1 text-secondary" />
                  {email}
                </small>
              </div>
            </div>

            <hr />

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <div>
                  <FaReceipt className="me-2 text-secondary" />
                  <strong>{t("sale.receipt.receiptNo")}</strong> {receiptData?.numero}
                </div>
                <div>
                  <FaCalendarAlt className="me-2 text-secondary" />
                  <strong>{t("sale.receipt.date")}</strong> {receiptData?.dateOperation}
                </div>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <div>
                  <FaUser className="me-2 text-secondary" />
                  <strong>{t("sale.receipt.seller")}</strong> {receiptData?.createdBy}
                </div>
                <div>
                  <FaUser className="me-2 text-secondary" />
                  <strong>{t("sale.receipt.client")}</strong>{" "}
                  {receiptData?.clientNom || "Passager"}
                </div>
              </div>
            </div>

            <hr />

            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("product.description")}</th>
                  <th className="text-end">{t("sale.receipt.price")}</th>
                  <th className="text-end">{t("sale.receipt.quantity")}</th>
                  <th className="text-end">{t("sale.receipt.unite")}</th>
                  <th className="text-end">{t("sale.receipt.total")}</th>
                </tr>
              </thead>
              <tbody>
                {receiptData?.lignes?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.articleNom}</td>
                    <td className="text-end">{formatPrice(item.prixUnitaireTTC, false)}</td>
                    <td className="text-end">{item.quantiteLivree}</td>
                    <td className="text-end">
                      {unites.find((u) => u.code === item.unite)?.libelle || item.unite || "-"}
                    </td>
                    <td className="text-end">{formatPrice(item.montantTTC, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <hr />

            <div className="text-end mb-4">
              <div className="mb-2">
                <strong className="me-3">{t("sale.receipt.totalQuantity")}</strong>
                <span className="text-success fw-bold">{getTotalQuantitiesFromReceipt(receiptData)}</span>
              </div>
              <div className="mb-2">
                <strong className="me-3">{t("sale.receipt.total")}</strong>
                {formatPrice(receiptData?.montantTotalTTC)}
              </div>
            </div>

            <div className="text-center mt-4">
              <p className="mb-1">{t("sale.receipt.thankYou")}</p>
              <small className="text-muted">{t("sale.receipt.footer")}</small>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="pos-receipt-footer">
          <Button color="primary" onClick={() => generatePDF("THERMIQUE")}>
            Imprimer Ticket
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={searchModeModal} toggle={() => setSearchModeModal(false)} centered>
        <ModalHeader toggle={() => setSearchModeModal(false)}>
          Choisir le mode de recherche
        </ModalHeader>

        <ModalBody>
          <div className="d-grid gap-3">
            {SEARCH_MODES.map((m) => (
              <Button
                key={m.value}
                color={searchMode === m.value ? "primary" : "light"}
                className="py-3 fw-bold"
                onClick={() => {
                  setSearchMode(m.value);
                  setSearchModeModal(false);

                  // reset page + refresh search
                  setCurrentPage(1);
                  debouncedSearchRef.current(searchTerm, 0, m.value);
                }}
              >
                {m.label}
              </Button>
            ))}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setSearchModeModal(false)}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>


      {/* -----------------------------
         PAYMENT MODAL
      ----------------------------- */}
      <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="xl">
        <ModalHeader toggle={() => setPaymentModal(false)}>{t("sale.confirmSale")}</ModalHeader>
        <ModalBody>
          <Card className="border-0 shadow-sm bg-light bg-gradient mb-3">
            <CardBody>
              <Row className="mb-3">
                <Col md="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-2 text-muted fw-bold text-uppercase small">
                    <FaMoneyBillWave className="me-2 text-success" /> {t("payment.totalDue")}
                  </div>
                  <div className="fs-3 fw-bold text-success">{formatPrice(montantTotalTTC)}</div>
                </Col>

                <Col md="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-2 text-muted fw-bold text-uppercase small">
                    <FaCreditCard className="me-2 text-primary" /> {t("payment.method")}
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {/* ESPECES */}
                    <Button
                      color={formik.values.modeReglement === "ESPECES" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "ESPECES");
                        // reset specific fields
                        formik.setFieldValue("montantPayeCash", montantTotalTTC);
                        formik.setFieldValue("montantPayeCarte", 0);
                        formik.setFieldValue("montantPaye", montantTotalTTC);
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaMoneyBillWave className="me-2" /> Espèces
                    </Button>

                    {/* CARTE */}
                    <Button
                      color={formik.values.modeReglement === "CARTE_BANCAIRE" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "CARTE_BANCAIRE");
                        // auto paid full
                        formik.setFieldValue("montantPaye", montantTotalTTC);
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaCreditCard className="me-2" /> Carte
                    </Button>

                    {/* CHEQUE */}
                    <Button
                      color={formik.values.modeReglement === "CHEQUE" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "CHEQUE");
                        formik.setFieldValue("montantPaye", montantTotalTTC); // cheque usually = full
                        formik.setFieldValue("referenceVirement", "");
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaIdCard className="me-2" /> Chèque
                    </Button>

                    {/* VIREMENT */}
                    <Button
                      color={formik.values.modeReglement === "VIREMENT" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "VIREMENT");
                        formik.setFieldValue("montantPaye", montantTotalTTC); // generally = full
                        formik.setFieldValue("numeroCheque", "");
                        formik.setFieldValue("banque", "");
                        formik.setFieldValue("dateEcheanceCheque", null);
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaBuilding className="me-2" /> Virement
                    </Button>

                    {/* EN COMPTE */}
                    <Button
                      color={formik.values.modeReglement === "EN_COMPTE" ? "primary" : "secondary"}
                      onClick={() => {
                        formik.setFieldValue("modeReglement", "EN_COMPTE");
                        formik.setFieldValue("montantPaye", 0);
                        formik.setFieldValue("referenceVirement", "");
                        formik.setFieldValue("numeroCheque", "");
                        formik.setFieldValue("banque", "");
                        formik.setFieldValue("dateEcheanceCheque", null);
                      }}
                      className="d-flex align-items-center px-4 py-2"
                    >
                      <FaReceipt className="me-2" /> En compte
                    </Button>
                  </div>


                  {formik.touched.modeReglement && formik.errors.modeReglement && (
                    <div className="invalid-feedback d-block mt-2">{formik.errors.modeReglement}</div>
                  )}
                </Col>
              </Row>

              <Row className="mb-3">
                {formik.values.modeReglement !== "EN_COMPTE" && (<Col md="6">
                  <FormGroup>
                    <Label className="fw-bold text-muted">{t("payment.amountPaid")}</Label>
                    <Input
                      type="text"
                      name="montantPaye"
                      value={formik.values.montantPaye}
                      readOnly
                      inputMode="none"
                      onKeyDown={(e) => e.preventDefault()}
                      onClick={() => setPayKeypadOpen(true)}
                      className="py-2 fs-5 pos-nokeyboard fw-bold"
                    />

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {[50, 100, 200, 500, 1000].map((v) => (
                        <Button
                          key={v}
                          color="light"
                          className="fw-bold px-3"
                          onClick={() => formik.setFieldValue("montantPaye", v)}
                        >
                          {v} DH
                        </Button>
                      ))}
                      <Button color="secondary" outline className="fw-bold px-3" onClick={() => setPayKeypadOpen(true)}>
                        Saisir
                      </Button>
                    </div>
                  </FormGroup>
                </Col>)}

                {formik.values.modeReglement === "ESPECES" && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="fw-bold text-muted">{t("payment.change")}</Label>
                      <Input
                        type="number"
                        value={((formik.values.montantPaye || montantTotalTTC) - montantTotalTTC).toFixed(2)}
                        disabled
                        className="bg-light text-success py-2 fs-5"
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {/* {formik.values.modeReglement === "EN_COMPTE" && (
                <Row className="mt-3">
                  <Col md="6">
                    <FormGroup>
                      <Label className="fw-bold text-muted">Note</Label>
                      <Input
                        value={formik.values.notePaiement}
                        onChange={(e) => formik.setFieldValue("notePaiement", e.target.value)}
                        placeholder="Paiement à crédit / à régler plus tard..."
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )} */}

            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="d-flex justify-content-end">
          <Button color="primary" size="lg" className="px-4" onClick={() => handleCreateSale(formik.values)}>
            {t("payment.confirm")}
          </Button>
          <Button color="secondary" size="lg" className="px-4 ms-2" onClick={() => setPaymentModal(false)}>
            {t("payment.cancel")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* CLEAR CART */}
      <Modal isOpen={clearCartModal} toggle={toggleClearCartModal} size="xs">
        <ModalHeader toggle={toggleClearCartModal}>{t("cart.clearConfirmTitle")}</ModalHeader>
        <ModalBody>{t("cart.clearConfirmMessage")}</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleClearCart}>
            {t("cart.confirm")}
          </Button>
          <Button color="secondary" onClick={toggleClearCartModal}>
            {t("cart.cancel")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* SEARCH KEYPAD */}
      <POSKeypadModal
        isOpen={showKeypad}
        toggle={() => setShowKeypad(false)}
        value={searchTerm}
        onChange={(val) => {
          setSearchTerm(val);
          debouncedSearchRef.current(val, currentPage - 1, searchMode);
        }}
        onSearch={() => {
          refreshProducts(searchTerm, 0, searchMode);
          setCurrentPage(1);
        }}
        title={`Recherche Article (${currentSearchModeLabel})`}
      />

      <POSKeypadModal
        isOpen={clientTextKeypad.isOpen}
        toggle={closeClientTextKeypad}
        value={clientTextKeypad.field ? client[clientTextKeypad.field] || "" : ""}
        onChange={handleClientTextKeypadChange}
        title={clientTextKeypad.title}
        showSearchButton={false}
      />

      {/* CART NUMERIC KEYPAD (QTY / PRICE) */}
      <POSNumericKeypadModal
        isOpen={numPadOpen}
        toggle={() => setNumPadOpen(false)}
        title={numPadConfig.title}
        value={numPadConfig.value}
        allowDecimal={true}
        onConfirm={(val) => {
          const parsed = Number(val || 0);
          if (Number.isNaN(parsed)) return;

          handleInputChange(numPadConfig.index, {
            target: { name: numPadConfig.field, value: parsed },
          });
        }}
      />

      <POSNumericKeypadModal
        isOpen={keypadOpen}
        toggle={() => setKeypadOpen(false)}
        title={
          keypadTarget === "cash"
            ? "Montant espèces"
            : "Montant carte"
        }
        value={
          keypadTarget === "cash"
            ? formik.values.montantPayeCash
            : formik.values.montantPayeCarte
        }
        allowDecimal={true}
        maxLength={10}
        onConfirm={handleKeypadConfirm}
      />

      {/* PAY KEYPAD */}
      <POSNumericKeypadModal
        isOpen={payKeypadOpen}
        toggle={() => setPayKeypadOpen(false)}
        title="Montant payé"
        value={formik.values.montantPaye}
        allowDecimal={true}
        onConfirm={(val) => {
          const amount = Number(val || 0);
          if (Number.isNaN(amount)) return;
          formik.setFieldValue("montantPaye", amount);
        }}
      />

      {/* CLIENT SELECT MODAL */}
      <ClientSelectModal
        isOpen={clientSelectModal}
        toggle={() => setClientSelectModal(false)}
        clients={clientOptions}
        selectedClient={selectedClient}
        onSelect={(c) => setSelectedClient(c)}
        onAddNew={() => {
          setClientSelectModal(false);
          closeClientTextKeypad();
          setShowClientModal(true);
        }}
      />

      {/* =============================
   CLIENT CREATE POS MODAL
============================= */}
      <Modal
        isOpen={showClientModal}
        toggle={() => {
          closeClientTextKeypad();
          setShowClientModal(false);
        }}
        size="xl"
        centered
        className="pos-client-modal"
      >
        <ModalHeader
          toggle={() => {
            closeClientTextKeypad();
            setShowClientModal(false);
          }}
          className="border-0 pb-0"
        >
          <div className="d-flex flex-column">
            <div className="fw-bold fs-4">{t("client.newClient")}</div>
            <small className="text-muted">Ajoutez rapidement un client</small>
          </div>
        </ModalHeader>

        <ModalBody className="pt-2">
          <Card className="border-0 shadow-sm rounded-4">
            <CardBody className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <Label className="fw-bold">
                    <FaBuilding className="me-2 text-muted" />
                    Entreprise *
                  </Label>
                  <Input
                    name="entreprise"
                    value={client.entreprise}
                    readOnly
                    inputMode="none"
                    placeholder="Nom de l'entreprise"
                    invalid={!!clientErrors.entreprise}
                    className="py-2 rounded-3 pos-nokeyboard"
                    onFocus={(e) => {
                      e.target.blur();
                      openClientTextKeypad("entreprise", "Entreprise");
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      openClientTextKeypad("entreprise", "Entreprise");
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      openClientTextKeypad("entreprise", "Entreprise");
                    }}
                    onClick={() => openClientTextKeypad("entreprise", "Entreprise")}
                  />
                  {clientErrors.entreprise && (
                    <div className="text-danger small mt-1">{clientErrors.entreprise}</div>
                  )}
                </Col>
                <Col md={6}>
                  <Label className="fw-bold">
                    <FaIdCard className="me-2 text-muted" />
                    ICE *
                  </Label>
                  <Input
                    name="ice"
                    value={client.ice}
                    readOnly
                    inputMode="none"
                    placeholder="Numéro ICE"
                    invalid={!!clientErrors.ice}
                    className="py-2 rounded-3 pos-nokeyboard"
                    onFocus={(e) => {
                      e.target.blur();
                      openClientTextKeypad("ice", "ICE");
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      openClientTextKeypad("ice", "ICE");
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      openClientTextKeypad("ice", "ICE");
                    }}
                    onClick={() => openClientTextKeypad("ice", "ICE")}
                  />
                  {clientErrors.ice && (
                    <div className="text-danger small mt-1">{clientErrors.ice}</div>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ModalBody>

        {/* POS FOOTER */}
        <ModalFooter className="border-0 d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            className="fw-bold px-4 py-2 rounded-3"
            onClick={() => {
              closeClientTextKeypad();
              setShowClientModal(false);
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            color="primary"
            className="fw-bold px-4 py-2 rounded-3"
            onClick={handleSubmitClient}
          >
            {t("common.save")}
          </Button>
        </ModalFooter>
      </Modal>


    </Container>
  );
};

export default SaleForm;
